import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
/**
 * Thrown by fetchLocation() specifically when the user has denied (or not granted) location
 * permission — as opposed to any other failure (e.g. a GPS timeout), which surfaces as whatever
 * error Geolocation.getCurrentPosition() itself throws. Callers can distinguish the two to avoid
 * showing a "permission" message for a failure that has nothing to do with permissions.
 */
export class LocationPermissionDeniedError extends Error {
    constructor() {
        super('Location permission not granted');
        this.name = 'LocationPermissionDeniedError';
    }
}
// Capacitor's own default (10s) is too short for a cold GPS fix with enableHighAccuracy, especially
// indoors — that's what was forcing repeated manual retries. maximumAge lets a retry made shortly
// after an almost-successful attempt return the position the OS already acquired, instead of
// forcing a brand new fix from scratch each time.
const POSITION_TIMEOUT_MS = 20000;
const POSITION_MAX_AGE_MS = 10000;
// The W3C Geolocation API's own PERMISSION_DENIED code — navigator.geolocation's rejection is
// re-thrown as-is by Capacitor's web implementation, so this is what a browser-level (as opposed
// to OS-level, which checkPermissions()/requestPermissions() already cover above) permission
// failure looks like once getCurrentPosition() itself rejects.
const GEOLOCATION_PERMISSION_DENIED_CODE = 1;
/**
 * Fetches the current device location.
 * On native (iOS/Android), requests a high-accuracy GPS fix via the Capacitor GPS plugin.
 * On web/desktop, requests standard (not high) accuracy — desktop/laptop browsers have no GPS
 * chip, so enableHighAccuracy makes them keep waiting for a GPS-grade fix they can never produce,
 * which commonly times out or reports the position as unavailable even with permission granted
 * and a WiFi/IP-based fix readily available.
 *
 * On native, explicitly awaits the permission decision before requesting a position. Calling
 * getCurrentPosition() directly while permission is still 'prompt' races the OS permission
 * dialog: the in-flight request can fail before the user responds, even though a second call
 * made after the dialog is dismissed would succeed immediately.
 *
 * On web this check/request step is skipped entirely: the web Geolocation API has no separate
 * permission-request call — Capacitor's own web implementation of requestPermissions() is
 * unimplemented and rejects immediately with no prompt shown. Calling getCurrentPosition()
 * directly is what triggers the browser's permission prompt on web.
 *
 * @returns A promise resolving to the Capacitor GeolocationPosition.
 */
export const fetchLocation = async () => {
    if (Capacitor.isNativePlatform()) {
        const status = await Geolocation.checkPermissions();
        if (status.location !== 'granted') {
            const requested = await Geolocation.requestPermissions();
            if (requested.location !== 'granted') {
                throw new LocationPermissionDeniedError();
            }
        }
    }
    try {
        return await Geolocation.getCurrentPosition({
            enableHighAccuracy: Capacitor.isNativePlatform(),
            timeout: POSITION_TIMEOUT_MS,
            maximumAge: POSITION_MAX_AGE_MS,
        });
    }
    catch (error) {
        // checkPermissions() only reflects the site's own permission grant — on web it can report
        // 'granted' even though the OS itself has location services disabled for the browser
        // (e.g. macOS System Settings > Privacy & Security > Location Services). That surfaces
        // here as an immediate PERMISSION_DENIED rejection instead of a real GPS/network timeout.
        if (typeof error === 'object' &&
            error &&
            'code' in error &&
            error.code === GEOLOCATION_PERMISSION_DENIED_CODE) {
            throw new LocationPermissionDeniedError();
        }
        throw error;
    }
};
